import { BUILDING_NAMES } from "./buildings.js";
import { RESOURCE_NAMES } from "./resources.js";
import { TUTORIAL_DARK_MATTER_REWARD } from "./premium.js";
import { SUN_ATTACK_COOLDOWN_HOURS, SUN_ATTACK_MIN_POPULATION } from "./fleets.js";
import { ALLIANCE_REQUIRED_BUILDING_LEVEL } from "./alliances.js";
import { TEAM_PLANET_REQUIRED_BUILDING_LEVEL } from "./teamPlanets.js";
import { SUN_CONTROL_CLAIM_POPULATION, SUN_CONTROL_CLAIM_WINDOW_HOURS, SUN_CONTROL_MAINTAIN_POPULATION, SUN_CONTROL_RECLAIM_IMMUNITY_HOURS, SUN_CONTROL_TOP_UP_WINDOW_HOURS, SUN_VICTORY_CONTEST_HOURS, SUNS_TO_WIN, } from "./sunControl.js";
const formatCount = (value) => value.toLocaleString("en-US");
/**
 * The new-player tutorial. `getTutorialSteps(race)` is the single entry
 * point — a shared prefix (planet basics, resource/industry/military zone
 * setup, first ship) is identical for every race, then each race continues
 * with its own sequence, since ship (and therefore science/building-level)
 * requirements diverge per race from that point on. See
 * `docs/game-details/tutorial.md` for the full design rationale.
 *
 * Every non-manual step's `reward` funds the *next* step's real resource
 * cost (`calculateBuildingUpgradeCost`/`calculateScienceCost`/ship
 * `cost`, summed across every level the next step spans) — never the
 * current step's own cost. Steps in the shared prefix grant 100% of that
 * cost; every step past the race split grants 50%. Every reward is capped
 * at 70,000 per resource. These are exact, not flavor — see
 * `docs/game-details/tutorial.md` for how they were derived, and re-derive
 * them (a throwaway Vitest script in `packages/game-engine` walking the
 * same formulas is the fastest way) if a cost formula or a step's ordering
 * ever changes. Every race's rewards additionally carry a modest hand-tuned
 * boost on top of the formula baseline (~15%, rounded up to a clean number
 * — still capped at 70,000).
 *
 * The team chapter (`TEAM_TUTORIAL_STEPS`, appended after every race's path)
 * is the exception: its resource rewards are hand-picked, not funded by any
 * next-step cost formula.
 */
export const SHARED_TUTORIAL_STEPS = [
    {
        id: "welcome",
        title: "Welcome, Commander!",
        body: "Every empire starts with a single world. Let's walk through the basics: managing your planet, producing resources, researching new technology, and building your first fleet.",
        route: "/planet",
        completion: { type: "manual" },
    },
    {
        id: "resources1",
        title: "Your resource bar",
        body: `${RESOURCE_NAMES.metal}, ${RESOURCE_NAMES.minerals}, and ${RESOURCE_NAMES.gas} are produced automatically by your buildings and stockpiled up to your storage capacity.`,
        route: "/planet",
        targetId: "resource-bar-left",
        completion: { type: "manual" },
    },
    {
        id: "resources2",
        title: "Your resource bar",
        body: `Energy powers those buildings, Population crews your ships, and ${RESOURCE_NAMES.scrap} comes from destroyed wreckage.`,
        route: "/planet",
        targetId: "resource-bar-right",
        completion: { type: "manual" },
    },
    {
        id: "resources3",
        title: RESOURCE_NAMES.ozone,
        body: `${RESOURCE_NAMES.ozone} keeps your planets habitable. Every planet starts with 15,000 units of ${RESOURCE_NAMES.ozone}, and the more buildings you build on it, the more ${RESOURCE_NAMES.ozone} they consume. Keep an eye on it, but don't worry: there are ways to recover ${RESOURCE_NAMES.ozone} on your planets. We will check it out later.`,
        route: "/planet",
        targetId: "ozone-resource",
        completion: { type: "manual" },
    },
    {
        id: "planetZones",
        title: "Zones",
        body: "Your planet is organized into three zones where you can control your resource production, expand your planet's outputs by industry, and improve your empire's military capabilities. Let's dive in.",
        route: "/planet",
        targetId: "zone-links",
        completion: { type: "manual" },
        reward: { type: "resources", amounts: { metal: 24, minerals: 10 } },
    },
    {
        id: "buildMetalMine",
        title: `Upgrade the ${BUILDING_NAMES.metalMine}`,
        body: `${RESOURCE_NAMES.metal} is the main building block of your empire. Every level increases ${RESOURCE_NAMES.metal} production. Bring it up to level 1 to get an early income.`,
        route: "/zone/resource",
        targetId: "building-metalMine",
        completion: { type: "buildingLevel", building: "metalMine", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 24, minerals: 12 } },
    },
    {
        id: "buildMineralMine",
        title: `Upgrade the ${BUILDING_NAMES.mineralRefinery}`,
        body: `${RESOURCE_NAMES.minerals} are just as important as ${RESOURCE_NAMES.metal}. Most buildings and ships need both. Bring the ${BUILDING_NAMES.mineralRefinery} up to level 1.`,
        route: "/zone/resource",
        targetId: "building-mineralRefinery",
        completion: { type: "buildingLevel", building: "mineralRefinery", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 112, minerals: 35 } },
    },
    {
        id: "buildGasPlant",
        title: `Upgrade the ${BUILDING_NAMES.gasPlant}`,
        body: `${RESOURCE_NAMES.gas} becomes essential once you start researching sciences and building advanced ships. Your fleets also require ${RESOURCE_NAMES.gas} to go on missions. Bring the ${BUILDING_NAMES.gasPlant} up to level 1.`,
        route: "/zone/resource",
        targetId: "building-gasPlant",
        completion: { type: "buildingLevel", building: "gasPlant", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 75, minerals: 30 } },
    },
    {
        id: "buildSolarPlant",
        title: `Build the ${BUILDING_NAMES.solarPlant}`,
        body: `Buildings consume Energy to operate — the ${BUILDING_NAMES.solarPlant} is your first source of it. Bring it up to level 1 so future upgrades don't stall for lack of power.`,
        route: "/zone/resource",
        targetId: "building-solarPlant",
        completion: { type: "buildingLevel", building: "solarPlant", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 400, minerals: 120, gas: 200 } },
    },
    {
        id: "buildFactory",
        title: `Build the ${BUILDING_NAMES.robotFactory}`,
        body: `The ${BUILDING_NAMES.robotFactory} produces robots that speed up construction and boost production. It's also a prerequisite for the ${BUILDING_NAMES.laboratory} and ${BUILDING_NAMES.hangars}, so build it before continuing.`,
        route: "/zone/industry",
        targetId: "building-robotFactory",
        completion: { type: "buildingLevel", building: "robotFactory", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 2000 } },
    },
    {
        id: "buildMetalStorage",
        title: `Build ${BUILDING_NAMES.metalStorage}`,
        body: `${BUILDING_NAMES.metalStorage} raises how much ${RESOURCE_NAMES.metal} you can stockpile before production caps out. Bring it to level 1.`,
        route: "/zone/industry",
        targetId: "building-metalStorage",
        completion: { type: "buildingLevel", building: "metalStorage", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 1500, minerals: 500 } },
    },
    {
        id: "buildMineralStorage",
        title: `Build ${BUILDING_NAMES.mineralStorage}`,
        body: `${BUILDING_NAMES.mineralStorage} does the same for ${RESOURCE_NAMES.minerals}. Bring it to level 1.`,
        route: "/zone/industry",
        targetId: "building-mineralStorage",
        completion: { type: "buildingLevel", building: "mineralStorage", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 2000 } },
    },
    {
        id: "buildGasStorage",
        title: `Build the ${BUILDING_NAMES.gasStorage}`,
        body: `The ${BUILDING_NAMES.gasStorage} rounds out your storage — ${RESOURCE_NAMES.metal}, ${RESOURCE_NAMES.minerals}, and ${RESOURCE_NAMES.gas} all covered. Bring it to level 1.`,
        route: "/zone/industry",
        targetId: "building-gasStorage",
        completion: { type: "buildingLevel", building: "gasStorage", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 200, minerals: 35 } },
    },
    {
        id: "buildHangars",
        title: `Build the ${BUILDING_NAMES.hangars}`,
        body: `The ${BUILDING_NAMES.hangars} provides population capacity for your ships and defences. This building requires the ${BUILDING_NAMES.robotFactory}, which you just built.`,
        route: "/zone/resource",
        targetId: "building-hangars",
        completion: { type: "buildingLevel", building: "hangars", minLevel: 1 },
    },
    {
        id: "sciencesPreamble",
        title: "Sciences",
        body: `Great. Now that you have sorted out your planet income, it's time to look at science research. You can go to the ${BUILDING_NAMES.laboratory} by clicking on the building icon here in the Military zone.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "manual" },
        reward: { type: "resources", amounts: { metal: 170, minerals: 340, gas: 170 } },
    },
    {
        id: "buildLaboratory",
        title: `Build the ${BUILDING_NAMES.laboratory}`,
        body: `The ${BUILDING_NAMES.laboratory}, found in the Military Zone, is required before you can research anything. Upgrade it to unlock the Sciences page.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 500, minerals: 250, gas: 100 } },
    },
    {
        id: "buildShipyard",
        title: `Build the ${BUILDING_NAMES.shipyard}`,
        body: `The ${BUILDING_NAMES.shipyard}, also in the Military Zone, is where you'll construct every ship. Keep upgrading it later on — bigger ships need a higher ${BUILDING_NAMES.shipyard} level.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 250, minerals: 150, gas: 300 } },
    },
    {
        id: "buildStarport",
        title: `Build the ${BUILDING_NAMES.techCenter}`,
        body: `The ${BUILDING_NAMES.techCenter} upgrades your existing ships once they're built. Bring it to level 1.`,
        route: "/zone/military",
        targetId: "building-techCenter",
        completion: { type: "buildingLevel", building: "techCenter", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 2000, minerals: 1500, gas: 650 } },
    },
    {
        id: "buildGovernment",
        title: `Build the ${BUILDING_NAMES.government}`,
        body: `The ${BUILDING_NAMES.government} is the political building you'll need to manage your alliance and Team planet later on. Bring it to level 1.`,
        route: "/zone/military",
        targetId: "building-government",
        completion: { type: "buildingLevel", building: "government", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 5000, minerals: 3500, gas: 100 } },
    },
    {
        id: "buildTradeCenter",
        title: `Build the ${BUILDING_NAMES.tradeCenter}`,
        body: `The ${BUILDING_NAMES.tradeCenter} lets you exchange one resource for another — one trade per 6 hours per level. Bring it to level 1.`,
        route: "/zone/industry",
        targetId: "building-tradeCenter",
        completion: { type: "buildingLevel", building: "tradeCenter", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 1000, minerals: 400 } },
    },
    {
        id: "scienceMathematics1",
        title: "Research Mathematics",
        body: "Mathematics increases the resources you gain on your planets, and is a building block for later sciences. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-mathematics",
        completion: { type: "scienceLevel", science: "mathematics", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 1200, minerals: 600, gas: 300 } },
    },
    {
        id: "buildShipyardL2",
        title: "Building better ships",
        body: `If you want to build better ships you need higher levels of the ${BUILDING_NAMES.shipyard}. Upgrade your ${BUILDING_NAMES.shipyard} to level 2.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 2000, minerals: 800 } },
    },
];
// ---------------------------------------------------------------------------
// Race-specific continuations. Each begins with Mathematics 2 → Ship #1
// (identical content, but Ship #1's cost — and so the Mathematics-2 reward
// funding it — differs for Axons) and ends with exploreMore and the alliance introduction.
//
// Sciences are ordered by ascending Research Center-level requirement (their
// "tier") first, ship-driven need second — e.g. Architecture only
// needs Research Center 4, so it's researched right alongside Intelligence/Radar
// Systems/Ecology the moment Research Center 4 is reached, with Physics and
// Mathematics bumped straight to whatever level it needs (5) at that same
// point, rather than waiting for some *other*, higher-tier thing to
// eventually pull them there. Getting this wrong once already: Ultimate
// Building and Physics 5 had drifted all the way down to the Research Center
// 9-10 tail purely because nothing *else* happened to need Physics past 4
// until much later — nothing was wrong with their own gates, they were just
// stranded behind unrelated, higher-tier ship requirements. Laser/Ion/
// Plasma + Polymer Shields get the same treatment: all four only need
// Research Center 8, so they're researched as one block the moment Research Center 8
// is reached (Plasma's own gate — Mathematics 7, Laser 10,
// Ion 5 — is satisfied inline, in the same block), rather than
// splitting Laser/Ion into an early partial bump now and a second
// bump much later once Plasma finally got around to needing it.
// ---------------------------------------------------------------------------
const ALLIANCE_INTRO_STEP = {
    id: "allianceIntro",
    title: "Join forces",
    body: `Rounds are won by alliances, not lone commanders: the first alliance to take control of Suns and hold ${SUNS_TO_WIN} of them through a ${SUN_VICTORY_CONTEST_HOURS}-hour countdown wins. The next steps walk you through it — joining an alliance, founding its Team planet, sending a Sun Attack, taking Suns and winning the round.`,
    route: "/alliance",
    completion: { type: "manual" },
    reward: {
        type: "premium",
        label: "14-day Premium boost",
        grant: { boost: { tier: "premium", days: 14 }, darkMatter: TUTORIAL_DARK_MATTER_REWARD },
    },
};
const HUMANS_STEPS = [
    {
        id: "scienceMathematics2",
        title: "Research Mathematics",
        body: "You need more resources. Research Mathematics up to level 2 to earn more per hour.",
        route: "/sciences",
        targetId: "science-mathematics",
        completion: { type: "scienceLevel", science: "mathematics", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 3000, minerals: 1750 } },
    },
    {
        id: "shipCargo",
        title: "Build a Courier",
        body: "Let's build a light transport ship for moving resources between your planets. Build at least one.",
        route: "/shipyard",
        targetId: "ship-1",
        completion: { type: "shipBuilt", shipId: 1, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 350, minerals: 700, gas: 350 } },
    },
    {
        id: "shipCargoUpgrade",
        title: "Upgrade your Courier",
        body: `The ${BUILDING_NAMES.techCenter} you built earlier upgrades ships — improvements apply globally, to every Courier you own or build from now on. Upgrade it to level 1 for +10% Cargo.`,
        route: "/starport",
        targetId: "ship-upgrade-1",
        completion: { type: "shipUpgradeLevel", shipId: 1, minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 1200 } },
    },
    {
        id: "buildLaboratoryL2",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Astronomy needs a slightly better ${BUILDING_NAMES.laboratory}. Upgrade it to level 2.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 900, gas: 900 } },
    },
    {
        id: "scienceAstronomy",
        title: "Research Astronomy",
        body: "Astronomy increases the speed of your ships and is required for the Vanguard — and Intelligence, coming up shortly, needs it at level 2. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-astronomy",
        completion: { type: "scienceLevel", science: "astronomy", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 1500, minerals: 950 } },
    },
    {
        id: "shipScout",
        title: "Build a Vanguard",
        body: "The Vanguard is your fast, cheap first warship — light armor lets it slip past enemy armor entirely. Build at least one.",
        route: "/shipyard",
        targetId: "ship-3",
        completion: { type: "shipBuilt", shipId: 3, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 300, minerals: 600, gas: 300 } },
    },
    {
        id: "shipScoutUpgrade",
        title: "Upgrade your Vanguard",
        body: `War ships upgrade Attack and Life instead of Cargo. Upgrade your Vanguard to level 1 from the ${BUILDING_NAMES.techCenter} for +5% Attack and Life.`,
        route: "/starport",
        targetId: "ship-upgrade-3",
        completion: { type: "shipUpgradeLevel", shipId: 3, minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 2200, minerals: 600 } },
    },
    {
        id: "buildLaboratoryL3",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Chemistry needs an even better ${BUILDING_NAMES.laboratory}. Upgrade it to level 3.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 3 },
        reward: { type: "resources", amounts: { metal: 3500, minerals: 1750, gas: 450 } },
    },
    {
        id: "scienceChemistry",
        title: "Research Chemistry",
        body: "Chemistry reduces ship fuel consumption and unlocks Rocket Fuel research. Research it up to level 4.",
        route: "/sciences",
        targetId: "science-chemistry",
        completion: { type: "scienceLevel", science: "chemistry", minLevel: 4 },
        reward: { type: "resources", amounts: { metal: 500, minerals: 1000, gas: 500 } },
    },
    {
        id: "buildLaboratoryL4",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `A whole cluster of sciences — Intelligence, Signal Systems, Ecology, and Architecture — only need ${BUILDING_NAMES.laboratory} level 4. Upgrade it once and unlock all four.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 4 },
        reward: { type: "resources", amounts: { metal: 18000, minerals: 9000, gas: 750 } },
    },
    {
        id: "sciencePhysics",
        title: "Research Physics",
        body: "Physics increases Energy production, and Architecture (next up) needs it at level 5. Research it up to level 5.",
        route: "/sciences",
        targetId: "science-physics",
        completion: { type: "scienceLevel", science: "physics", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 16500, minerals: 6500 } },
    },
    {
        id: "scienceMathematicsL5",
        title: "Research Mathematics",
        body: "Architecture also needs Mathematics level 5. Research it up.",
        route: "/sciences",
        targetId: "science-mathematics",
        completion: { type: "scienceLevel", science: "mathematics", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 600, minerals: 450, gas: 150 } },
    },
    {
        id: "scienceEcology",
        title: "Research Ecology",
        body: `Ecology speeds up how quickly your planets recover ${RESOURCE_NAMES.ozone}. Research it up to level 1.`,
        route: "/sciences",
        targetId: "science-ecology",
        completion: { type: "scienceLevel", science: "ecology", minLevel: 1 },
        reward: { type: "resources", amounts: { minerals: 150, gas: 300 } },
    },
    {
        id: "scienceSignalSystems",
        title: "Research Signal Systems",
        body: "Signal Systems increases how many simultaneous fleet movements you can have in flight. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-signalSystems",
        completion: { type: "scienceLevel", science: "signalSystems", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 100, minerals: 200, gas: 100 } },
    },
    {
        id: "scienceIntelligence",
        title: "Research Intelligence",
        body: "Intelligence improves spying and is required for your Lurker. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-intelligence",
        completion: { type: "scienceLevel", science: "intelligence", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 5750, minerals: 3000 } },
    },
    {
        id: "scienceArchitecture",
        title: "Research Architecture",
        body: "Architecture reduces the cost of every building you upgrade from here on. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-architecture",
        completion: { type: "scienceLevel", science: "architecture", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 850, minerals: 1750, gas: 850 } },
    },
    {
        id: "buildLaboratoryL5",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Defence Systems needs a more advanced ${BUILDING_NAMES.laboratory}. Upgrade it to level 5.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 200, minerals: 100 } },
    },
    {
        id: "scienceDefenceSystems",
        title: "Research Defence Systems",
        body: "Defence Systems increases ship life and is required for the Saber. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-defenceSystems",
        completion: { type: "scienceLevel", science: "defenceSystems", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 1500, minerals: 3000, gas: 1500 } },
    },
    {
        id: "buildLaboratoryL6",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Rocket Fuel needs a more advanced ${BUILDING_NAMES.laboratory} still. Upgrade it to level 6.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 2250, minerals: 4250, gas: 850 } },
    },
    {
        id: "scienceRockets",
        title: "Research Rocket Fuel",
        body: "Rocket Fuel increases ship speed and is required by the Lurker. Research it up to level 3.",
        route: "/sciences",
        targetId: "science-rockets",
        completion: { type: "scienceLevel", science: "rockets", minLevel: 3 },
        reward: { type: "resources", amounts: { metal: 1500, minerals: 750, gas: 300 } },
    },
    {
        id: "buildShipyardL3",
        title: "Building better ships",
        body: `The Lurker needs an even better ${BUILDING_NAMES.shipyard}. Upgrade it to level 3.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 3 },
        reward: { type: "resources", amounts: { minerals: 600 } },
    },
    {
        id: "shipSpy",
        title: "Build a Lurker",
        body: "An expendable reconnaissance ship for scouting enemy planets before you commit a fleet. Build at least one.",
        route: "/shipyard",
        targetId: "ship-12",
        completion: { type: "shipBuilt", shipId: 12, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 6000, minerals: 5000 } },
    },
    {
        id: "shipCruiser",
        title: "Build a Saber",
        body: "A versatile mid-line warship with devastating critical strikes. Build at least one.",
        route: "/shipyard",
        targetId: "ship-4",
        completion: { type: "shipBuilt", shipId: 4, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 17500, gas: 17500 } },
    },
    {
        id: "scienceAstronomyL6",
        title: "Research Astronomy",
        body: "The Juggernaut, coming up next, needs Astronomy level 6.",
        route: "/sciences",
        targetId: "science-astronomy",
        completion: { type: "scienceLevel", science: "astronomy", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 3500, minerals: 1750, gas: 700 } },
    },
    {
        id: "buildShipyardL4",
        title: "Building better ships",
        body: `The Juggernaut and Pioneer both need a more advanced ${BUILDING_NAMES.shipyard}. Upgrade it to level 4.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 4 },
        reward: { type: "resources", amounts: { metal: 3750, minerals: 3000 } },
    },
    {
        id: "shipMegacargo",
        title: "The Juggernaut",
        body: "A heavy transport ship with a vast cargo hold — useful once you're moving serious resources between planets, but not required right now.",
        route: "/shipyard",
        targetId: "ship-2",
        completion: { type: "manual" },
        reward: { type: "resources", amounts: { metal: 7250, minerals: 14500, gas: 6250 } },
    },
    {
        id: "shipColony",
        title: "Build a Pioneer",
        body: "Settles new planets, founding a colony on arrival — how you expand your empire beyond one world. Build at least one.",
        route: "/shipyard",
        targetId: "ship-10",
        completion: { type: "shipBuilt", shipId: 10, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 16500, minerals: 32500, gas: 6500 } },
    },
    {
        id: "colonizeFlight",
        title: "Found your first colony",
        body: "Now send it out. Open the Galaxy map, tap an empty position, and launch a Colonize flight with your Pioneer. When it arrives, a brand-new planet joins your empire — switch between your worlds from the location button in the top bar.",
        route: "/galaxy",
        completion: { type: "fleetsSent", missionType: "colonize" },
        reward: {
            type: "premium",
            label: "3-day Premium boost",
            grant: { boost: { tier: "premium", days: 3 }, darkMatter: TUTORIAL_DARK_MATTER_REWARD },
        },
    },
    {
        id: "scienceRocketsL6",
        title: "Research Rocket Fuel",
        body: "The Salvator needs more Rocket Fuel. Research it up to level 6.",
        route: "/sciences",
        targetId: "science-rockets",
        completion: { type: "scienceLevel", science: "rockets", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 6250, minerals: 3250, gas: 1250 } },
    },
    {
        id: "shipRecycler",
        title: "Build a Salvator",
        body: `Harvests ${RESOURCE_NAMES.scrap} and debris left behind after battles — send it out after any fight to reclaim resources. Build at least one.`,
        route: "/shipyard",
        targetId: "ship-11",
        completion: { type: "shipBuilt", shipId: 11, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 7500, minerals: 3750, gas: 1500 } },
    },
    {
        id: "buildShipyardL5",
        title: "Building better ships",
        body: `The Aegis needs a more advanced ${BUILDING_NAMES.shipyard}. Upgrade it to level 5.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 6500, minerals: 13000, gas: 6500 } },
    },
    {
        id: "buildLaboratoryL8",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Laser, Ion, Plasma, and Polymer Shields — your gateway to stronger weapons and armor — all become available at once here. Upgrade the ${BUILDING_NAMES.laboratory} to level 8.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 8 },
        reward: { type: "resources", amounts: { metal: 70000, minerals: 59000 } },
    },
    {
        id: "scienceLaserL10",
        title: "Research Laser",
        body: "Laser increases the attack of laser-armed ships and is a building block for Ion and Plasma. Research it up to level 10.",
        route: "/sciences",
        targetId: "science-laser",
        completion: { type: "scienceLevel", science: "laser", minLevel: 10 },
        reward: { type: "resources", amounts: { metal: 9000, minerals: 4500, gas: 900 } },
    },
    {
        id: "scienceIonL5",
        title: "Research Ion",
        body: "Ion increases the attack of ion-armed ships and unlocks the Aegis. Research it up to level 5.",
        route: "/sciences",
        targetId: "science-ion",
        completion: { type: "scienceLevel", science: "ion", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 56000, minerals: 22500 } },
    },
    {
        id: "scienceMathematicsL7",
        title: "Research Mathematics",
        body: "Plasma needs Mathematics level 7. Research it up.",
        route: "/sciences",
        targetId: "science-mathematics",
        completion: { type: "scienceLevel", science: "mathematics", minLevel: 7 },
        reward: { type: "resources", amounts: { metal: 1750, minerals: 1750, gas: 1750 } },
    },
    {
        id: "sciencePlasma",
        title: "Research Plasma",
        body: "Plasma increases the attack of plasma-armed ships. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-plasma",
        completion: { type: "scienceLevel", science: "plasma", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 600, minerals: 300, gas: 150 } },
    },
    {
        id: "sciencePolymerShields",
        title: "Research Polymer Shields",
        body: "Polymer Shields increases the armor of light-hulled ships and defences. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-polymerShields",
        completion: { type: "scienceLevel", science: "polymerShields", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 3250, minerals: 9250 } },
    },
    {
        id: "shipGuardian",
        title: "Build your Aegis",
        body: "A defensive warship that bolsters your whole fleet's life. Build at least one.",
        route: "/shipyard",
        targetId: "ship-5",
        completion: { type: "shipBuilt", shipId: 5, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 11500, minerals: 5750, gas: 1500 } },
    },
    {
        id: "scienceChemistryL6",
        title: "Research Chemistry",
        body: `The ${BUILDING_NAMES.recyclingPlant} needs more Chemistry. Research it up to level 6.`,
        route: "/sciences",
        targetId: "science-chemistry",
        completion: { type: "scienceLevel", science: "chemistry", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 13000, minerals: 8500, gas: 3000 } },
    },
    {
        id: "buildRecyclingPlant",
        title: `Build the ${BUILDING_NAMES.recyclingPlant}`,
        body: `Converts ${RESOURCE_NAMES.scrap} into ships in parallel with your ${BUILDING_NAMES.shipyard}, speeding up production. Bring it to level 1.`,
        route: "/zone/industry",
        targetId: "building-recyclingPlant",
        completion: { type: "buildingLevel", building: "recyclingPlant", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 1750, minerals: 850 } },
    },
    {
        id: "scienceDefenceSystemsL5",
        title: "Research Defence Systems",
        body: "Aerodynamics needs more Defence Systems. Research it up to level 5.",
        route: "/sciences",
        targetId: "science-defenceSystems",
        completion: { type: "scienceLevel", science: "defenceSystems", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 54000, minerals: 27000, gas: 11000 } },
    },
    {
        id: "buildShipyardL7",
        title: "Building better ships",
        body: `The Bull needs a much more advanced ${BUILDING_NAMES.shipyard}. Upgrade it to level 7.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 7 },
        reward: { type: "resources", amounts: { minerals: 8750, gas: 4500 } },
    },
    {
        id: "scienceAerodynamics",
        title: "Research Aerodynamics",
        body: "Aerodynamics increases ship speed and is required for the Bull. Research it up to level 4.",
        route: "/sciences",
        targetId: "science-aerodynamics",
        completion: { type: "scienceLevel", science: "aerodynamics", minLevel: 4 },
        reward: { type: "resources", amounts: { metal: 28500, minerals: 12500 } },
    },
    {
        id: "shipBattlecruiser",
        title: "Build a Bull",
        body: "An armored line-breaker that hardens allied hulls. Build at least one.",
        route: "/shipyard",
        targetId: "ship-6",
        completion: { type: "shipBuilt", shipId: 6, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 7000, minerals: 14000, gas: 7000 } },
    },
    {
        id: "buildLaboratoryL9",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Carbon Fiber Shields needs ${BUILDING_NAMES.laboratory} level 9. Upgrade it.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 9 },
        reward: { type: "resources", amounts: { metal: 750, minerals: 400, gas: 200 } },
    },
    {
        id: "scienceCarbonFiberShields",
        title: "Research Carbon Fiber Shields",
        body: "Carbon Fiber Shields increases the armor of medium-hulled ships and defences. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-carbonFiberShields",
        completion: { type: "scienceLevel", science: "carbonFiberShields", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 12000, minerals: 23500, gas: 12000 } },
    },
    {
        id: "buildLaboratoryL10",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Heavy Metal Shields needs your highest ${BUILDING_NAMES.laboratory} level yet. Upgrade it to level 10.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 10 },
        reward: { type: "resources", amounts: { metal: 950, minerals: 500, gas: 250 } },
    },
    {
        id: "scienceHeavyMetalShields",
        title: "Research Heavy Metal Shields",
        body: "Heavy Metal Shields increases the armor of heavy-hulled ships and defences — the last science on this tour. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-heavyMetalShields",
        completion: { type: "scienceLevel", science: "heavyMetalShields", minLevel: 1 },
    },
    {
        id: "exploreMore",
        title: "More to explore",
        body: "Fleets, the Galaxy map, Alliances, and Rankings are all here too. Sending your fleet to attack, trade, or explore — and the battles that follow — are coming in future updates. For now, everything above is enough to build a strong, defensible empire.",
        route: "/planet",
        targetId: "bottom-nav",
        completion: { type: "manual" },
    },
    {
        id: "planetNameChange",
        title: "Rename your planet",
        body: "Give your homeworld a name of your own from its Edit Name button.",
        route: "/planet",
        targetId: "planet-edit-name",
        completion: { type: "manual" },
    },
    {
        id: "planetPhotoChange",
        title: "Change your planet's photo",
        body: "Pick a different look for your homeworld from its Change Photo button.",
        route: "/planet",
        targetId: "planet-edit-photo",
        completion: { type: "manual" },
    },
    {
        id: "profileNameChange",
        title: "Rename your commander",
        body: "Head to your Personal page and set your commander's display name — it's what other players see on the ranking board.",
        route: "/personal",
        targetId: "profile-edit-name",
        completion: { type: "manual" },
    },
    {
        id: "profilePhotoChange",
        title: "Add a profile photo",
        body: "Upload a photo so other commanders recognize you.",
        route: "/personal",
        targetId: "profile-edit-photo",
        completion: { type: "manual" },
    },
    ALLIANCE_INTRO_STEP,
];
const ROBOTS_STEPS = [
    {
        id: "scienceMathematics2",
        title: "Research Mathematics",
        body: "You need more resources. Research Mathematics up to level 2 to earn more per hour.",
        route: "/sciences",
        targetId: "science-mathematics",
        completion: { type: "scienceLevel", science: "mathematics", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 3000, minerals: 1750 } },
    },
    {
        id: "shipCargo",
        title: "Build a Lifter",
        body: "Let's build a light transport robot for moving resources between your planets. Build at least one.",
        route: "/shipyard",
        targetId: "ship-1",
        completion: { type: "shipBuilt", shipId: 1, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 350, minerals: 700, gas: 350 } },
    },
    {
        id: "shipCargoUpgrade",
        title: "Upgrade your Lifter",
        body: `The ${BUILDING_NAMES.techCenter} you built earlier upgrades ships — improvements apply globally, to every Lifter you own or build from now on. Upgrade it to level 1 for +10% Cargo.`,
        route: "/starport",
        targetId: "ship-upgrade-1",
        completion: { type: "shipUpgradeLevel", shipId: 1, minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 1200 } },
    },
    {
        id: "buildLaboratoryL2",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Astronomy needs a slightly better ${BUILDING_NAMES.laboratory}. Upgrade it to level 2.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 2250, gas: 2250 } },
    },
    {
        id: "scienceAstronomy",
        title: "Research Astronomy",
        body: "Astronomy increases the speed of your ships and is required for the Rivet. Research it up to level 3.",
        route: "/sciences",
        targetId: "science-astronomy",
        completion: { type: "scienceLevel", science: "astronomy", minLevel: 3 },
        reward: { type: "resources", amounts: { metal: 1500, minerals: 950 } },
    },
    {
        id: "shipScout",
        title: "Build a Rivet",
        body: "Your fast, cheap first warship — light armor lets it slip past enemy armor entirely. Build at least one.",
        route: "/shipyard",
        targetId: "ship-3",
        completion: { type: "shipBuilt", shipId: 3, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 300, minerals: 600, gas: 300 } },
    },
    {
        id: "shipScoutUpgrade",
        title: "Upgrade your Rivet",
        body: `War ships upgrade Attack and Life instead of Cargo. Upgrade your Rivet to level 1 from the ${BUILDING_NAMES.techCenter} for +5% Attack and Life.`,
        route: "/starport",
        targetId: "ship-upgrade-3",
        completion: { type: "shipUpgradeLevel", shipId: 3, minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 2000, minerals: 500 } },
    },
    {
        id: "buildLaboratoryL3",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Chemistry needs an even better ${BUILDING_NAMES.laboratory}. Upgrade it to level 3.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 3 },
        reward: { type: "resources", amounts: { metal: 3500, minerals: 1750, gas: 450 } },
    },
    {
        id: "scienceChemistry",
        title: "Research Chemistry",
        body: "Chemistry reduces ship fuel consumption and unlocks Rocket Fuel research. Research it up to level 4.",
        route: "/sciences",
        targetId: "science-chemistry",
        completion: { type: "scienceLevel", science: "chemistry", minLevel: 4 },
        reward: { type: "resources", amounts: { metal: 500, minerals: 1000, gas: 500 } },
    },
    {
        id: "buildLaboratoryL4",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `A whole cluster of sciences — Intelligence, Signal Systems, Ecology, and Architecture — only need ${BUILDING_NAMES.laboratory} level 4. Upgrade it once and unlock all four.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 4 },
        reward: { type: "resources", amounts: { metal: 18000, minerals: 9000, gas: 750 } },
    },
    {
        id: "sciencePhysics",
        title: "Research Physics",
        body: "Physics increases Energy production, and Architecture (next up) needs it at level 5. Research it up to level 5.",
        route: "/sciences",
        targetId: "science-physics",
        completion: { type: "scienceLevel", science: "physics", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 16500, minerals: 6500 } },
    },
    {
        id: "scienceMathematicsL5",
        title: "Research Mathematics",
        body: "Architecture also needs Mathematics level 5. Research it up.",
        route: "/sciences",
        targetId: "science-mathematics",
        completion: { type: "scienceLevel", science: "mathematics", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 600, minerals: 450, gas: 150 } },
    },
    {
        id: "scienceEcology",
        title: "Research Ecology",
        body: `Ecology speeds up how quickly your planets recover ${RESOURCE_NAMES.ozone}. Research it up to level 1.`,
        route: "/sciences",
        targetId: "science-ecology",
        completion: { type: "scienceLevel", science: "ecology", minLevel: 1 },
        reward: { type: "resources", amounts: { minerals: 150, gas: 300 } },
    },
    {
        id: "scienceSignalSystems",
        title: "Research Signal Systems",
        body: "Signal Systems increases how many simultaneous fleet movements you can have in flight. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-signalSystems",
        completion: { type: "scienceLevel", science: "signalSystems", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 100, minerals: 200, gas: 100 } },
    },
    {
        id: "scienceIntelligence",
        title: "Research Intelligence",
        body: "Intelligence improves spying and is required for your Monitor. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-intelligence",
        completion: { type: "scienceLevel", science: "intelligence", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 5750, minerals: 3000 } },
    },
    {
        id: "scienceArchitecture",
        title: "Research Architecture",
        body: "Architecture reduces the cost of every building you upgrade from here on. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-architecture",
        completion: { type: "scienceLevel", science: "architecture", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 850, minerals: 1750, gas: 850 } },
    },
    {
        id: "buildLaboratoryL5",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Defence Systems needs a more advanced ${BUILDING_NAMES.laboratory}. Upgrade it to level 5.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 75, minerals: 50 } },
    },
    {
        id: "scienceDefenceSystemsL1",
        title: "Research Defence Systems",
        body: "Defence Systems increases ship life and is required for the Piston. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-defenceSystems",
        completion: { type: "scienceLevel", science: "defenceSystems", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 1500, minerals: 3000, gas: 1500 } },
    },
    {
        id: "buildLaboratoryL6",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Rocket Fuel needs a more advanced ${BUILDING_NAMES.laboratory} still. Upgrade it to level 6.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 2250, minerals: 4250, gas: 850 } },
    },
    {
        id: "scienceRockets",
        title: "Research Rocket Fuel",
        body: "Rocket Fuel increases ship speed and is required by the Monitor. Research it up to level 3.",
        route: "/sciences",
        targetId: "science-rockets",
        completion: { type: "scienceLevel", science: "rockets", minLevel: 3 },
        reward: { type: "resources", amounts: { metal: 1500, minerals: 750, gas: 300 } },
    },
    {
        id: "buildShipyardL3",
        title: "Building better ships",
        body: `The Monitor needs an even better ${BUILDING_NAMES.shipyard}. Upgrade it to level 3.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 3 },
        reward: { type: "resources", amounts: { minerals: 600 } },
    },
    {
        id: "shipSpy",
        title: "Build a Monitor",
        body: "An expendable reconnaissance drone for scouting enemy planets before you commit a fleet. Build at least one.",
        route: "/shipyard",
        targetId: "ship-12",
        completion: { type: "shipBuilt", shipId: 12, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 4250, minerals: 3500 } },
    },
    {
        id: "shipCruiser",
        title: "Build a Piston",
        body: "A fast attack platform with devastating critical strikes. Build at least one.",
        route: "/shipyard",
        targetId: "ship-4",
        completion: { type: "shipBuilt", shipId: 4, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 16500, gas: 16500 } },
    },
    {
        id: "scienceAstronomyL6",
        title: "Research Astronomy",
        body: "The Heavy Lifter, coming up next, needs Astronomy level 6.",
        route: "/sciences",
        targetId: "science-astronomy",
        completion: { type: "scienceLevel", science: "astronomy", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 16500, minerals: 32500, gas: 6500 } },
    },
    {
        id: "scienceRocketsL6",
        title: "Research Rocket Fuel",
        body: "The Heavy Lifter (and the Collector, later) needs more Rocket Fuel. Research it up to level 6.",
        route: "/sciences",
        targetId: "science-rockets",
        completion: { type: "scienceLevel", science: "rockets", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 3500, minerals: 1750, gas: 700 } },
    },
    {
        id: "buildShipyardL4",
        title: "Building better ships",
        body: `The Heavy Lifter and Assembler both need a more advanced ${BUILDING_NAMES.shipyard}. Upgrade it to level 4.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 4 },
        reward: { type: "resources", amounts: { metal: 8750, minerals: 6750 } },
    },
    {
        id: "shipMegacargo",
        title: "The Heavy Lifter",
        body: "A massive freight platform with the largest hold of its class — useful once you're moving serious resources between planets, but not required right now.",
        route: "/shipyard",
        targetId: "ship-2",
        completion: { type: "manual" },
        reward: { type: "resources", amounts: { metal: 7250, minerals: 14500, gas: 6250 } },
    },
    {
        id: "shipColony",
        title: "Build an Assembler",
        body: "Settles new planets, founding a colony on arrival — how you expand your empire beyond one world. Build at least one.",
        route: "/shipyard",
        targetId: "ship-10",
        completion: { type: "shipBuilt", shipId: 10, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 150, minerals: 75 } },
    },
    {
        id: "colonizeFlight",
        title: "Found your first colony",
        body: "Now send it out. Open the Galaxy map, tap an empty position, and launch a Colonize flight with your Assembler. When it arrives, a brand-new planet joins your empire — switch between your worlds from the location button in the top bar.",
        route: "/galaxy",
        completion: { type: "fleetsSent", missionType: "colonize" },
        reward: {
            type: "premium",
            label: "3-day Premium boost",
            grant: { boost: { tier: "premium", days: 3 }, darkMatter: TUTORIAL_DARK_MATTER_REWARD },
        },
    },
    {
        id: "scienceDefenceSystems",
        title: "Research Defence Systems",
        body: "The Collector needs a bit more Defence Systems. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-defenceSystems",
        completion: { type: "scienceLevel", science: "defenceSystems", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 8500, minerals: 4500, gas: 1500 } },
    },
    {
        id: "shipRecycler",
        title: "Build a Collector",
        body: `Harvests ${RESOURCE_NAMES.scrap} and debris left behind after battles — send it out after any fight to reclaim resources. Build at least one.`,
        route: "/shipyard",
        targetId: "ship-11",
        completion: { type: "shipBuilt", shipId: 11, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 7500, minerals: 3750, gas: 1500 } },
    },
    {
        id: "buildShipyardL5",
        title: "Building better ships",
        body: `The Fortress needs a more advanced ${BUILDING_NAMES.shipyard}. Upgrade it to level 5.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 6500, minerals: 13000, gas: 6500 } },
    },
    {
        id: "buildLaboratoryL8",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Laser, Ion, Plasma, and Polymer Shields — your gateway to stronger weapons and armor — all become available at once here. Upgrade the ${BUILDING_NAMES.laboratory} to level 8.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 8 },
        reward: { type: "resources", amounts: { metal: 70000, minerals: 59000 } },
    },
    {
        id: "scienceLaserL10",
        title: "Research Laser",
        body: "Laser increases the attack of laser-armed ships and is a building block for Ion and Plasma. Research it up to level 10.",
        route: "/sciences",
        targetId: "science-laser",
        completion: { type: "scienceLevel", science: "laser", minLevel: 10 },
        reward: { type: "resources", amounts: { metal: 9000, minerals: 4500, gas: 900 } },
    },
    {
        id: "scienceIonL5",
        title: "Research Ion",
        body: "Ion increases the attack of ion-armed ships and unlocks the Fortress. Research it up to level 5.",
        route: "/sciences",
        targetId: "science-ion",
        completion: { type: "scienceLevel", science: "ion", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 56000, minerals: 22500 } },
    },
    {
        id: "scienceMathematicsL7",
        title: "Research Mathematics",
        body: "Plasma needs Mathematics level 7. Research it up.",
        route: "/sciences",
        targetId: "science-mathematics",
        completion: { type: "scienceLevel", science: "mathematics", minLevel: 7 },
        reward: { type: "resources", amounts: { metal: 1750, minerals: 1750, gas: 1750 } },
    },
    {
        id: "sciencePlasma",
        title: "Research Plasma",
        body: "Plasma increases the attack of plasma-armed ships. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-plasma",
        completion: { type: "scienceLevel", science: "plasma", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 600, minerals: 300, gas: 150 } },
    },
    {
        id: "sciencePolymerShields",
        title: "Research Polymer Shields",
        body: "Polymer Shields increases the armor of light-hulled ships and defences. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-polymerShields",
        completion: { type: "scienceLevel", science: "polymerShields", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 3750, minerals: 11000 } },
    },
    {
        id: "shipGuardian",
        title: "Build your Fortress",
        body: "A defensive automaton that bolsters your whole fleet's life. Build at least one.",
        route: "/shipyard",
        targetId: "ship-5",
        completion: { type: "shipBuilt", shipId: 5, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 11500, minerals: 5750, gas: 1500 } },
    },
    {
        id: "scienceChemistryL6",
        title: "Research Chemistry",
        body: `The ${BUILDING_NAMES.recyclingPlant} needs more Chemistry. Research it up to level 6.`,
        route: "/sciences",
        targetId: "science-chemistry",
        completion: { type: "scienceLevel", science: "chemistry", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 13000, minerals: 8500, gas: 3000 } },
    },
    {
        id: "buildRecyclingPlant",
        title: `Build the ${BUILDING_NAMES.recyclingPlant}`,
        body: `Converts ${RESOURCE_NAMES.scrap} into ships in parallel with your ${BUILDING_NAMES.shipyard}, speeding up production. Bring it to level 1.`,
        route: "/zone/industry",
        targetId: "building-recyclingPlant",
        completion: { type: "buildingLevel", building: "recyclingPlant", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 1750, minerals: 850 } },
    },
    {
        id: "scienceDefenceSystemsL5",
        title: "Research Defence Systems",
        body: "Aerodynamics needs more Defence Systems. Research it up to level 5.",
        route: "/sciences",
        targetId: "science-defenceSystems",
        completion: { type: "scienceLevel", science: "defenceSystems", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 70000, minerals: 69000, gas: 28000 } },
    },
    {
        id: "buildShipyardL8",
        title: "Building better ships",
        body: `The Lattice needs a much more advanced ${BUILDING_NAMES.shipyard}. Upgrade it to level 8.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 8 },
        reward: { type: "resources", amounts: { minerals: 4250, gas: 2250 } },
    },
    {
        id: "scienceAerodynamics",
        title: "Research Aerodynamics",
        body: "Aerodynamics increases ship speed and is required for the Lattice. Research it up to level 3.",
        route: "/sciences",
        targetId: "science-aerodynamics",
        completion: { type: "scienceLevel", science: "aerodynamics", minLevel: 3 },
        reward: { type: "resources", amounts: { metal: 25000, minerals: 11000 } },
    },
    {
        id: "shipBattlecruiser",
        title: "Build a Lattice",
        body: "A linked battle group that hardens allied hulls. Build at least one.",
        route: "/shipyard",
        targetId: "ship-6",
        completion: { type: "shipBuilt", shipId: 6, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 7000, minerals: 14000, gas: 7000 } },
    },
    {
        id: "buildLaboratoryL9",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Carbon Fiber Shields needs ${BUILDING_NAMES.laboratory} level 9. Upgrade it.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 9 },
        reward: { type: "resources", amounts: { metal: 750, minerals: 400, gas: 200 } },
    },
    {
        id: "scienceCarbonFiberShields",
        title: "Research Carbon Fiber Shields",
        body: "Carbon Fiber Shields increases the armor of medium-hulled ships and defences. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-carbonFiberShields",
        completion: { type: "scienceLevel", science: "carbonFiberShields", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 12000, minerals: 23500, gas: 12000 } },
    },
    {
        id: "buildLaboratoryL10",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Heavy Metal Shields needs your highest ${BUILDING_NAMES.laboratory} level yet. Upgrade it to level 10.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 10 },
        reward: { type: "resources", amounts: { metal: 950, minerals: 500, gas: 250 } },
    },
    {
        id: "scienceHeavyMetalShields",
        title: "Research Heavy Metal Shields",
        body: "Heavy Metal Shields increases the armor of heavy-hulled ships and defences — the last science on this tour. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-heavyMetalShields",
        completion: { type: "scienceLevel", science: "heavyMetalShields", minLevel: 1 },
    },
    {
        id: "exploreMore",
        title: "More to explore",
        body: "Fleets, the Galaxy map, Alliances, and Rankings are all here too. Sending your fleet to attack, trade, or explore — and the battles that follow — are coming in future updates. For now, everything above is enough to build a strong, defensible empire.",
        route: "/planet",
        targetId: "bottom-nav",
        completion: { type: "manual" },
    },
    {
        id: "planetNameChange",
        title: "Rename your planet",
        body: "Give your homeworld a name of your own from its Edit Name button.",
        route: "/planet",
        targetId: "planet-edit-name",
        completion: { type: "manual" },
    },
    {
        id: "planetPhotoChange",
        title: "Change your planet's photo",
        body: "Pick a different look for your homeworld from its Change Photo button.",
        route: "/planet",
        targetId: "planet-edit-photo",
        completion: { type: "manual" },
    },
    {
        id: "profileNameChange",
        title: "Rename your commander",
        body: "Head to your Personal page and set your commander's display name — it's what other players see on the ranking board.",
        route: "/personal",
        targetId: "profile-edit-name",
        completion: { type: "manual" },
    },
    {
        id: "profilePhotoChange",
        title: "Add a profile photo",
        body: "Upload a photo so other commanders recognize you.",
        route: "/personal",
        targetId: "profile-edit-photo",
        completion: { type: "manual" },
    },
    ALLIANCE_INTRO_STEP,
];
const AXONS_STEPS = [
    {
        id: "scienceMathematics2",
        title: "Research Mathematics",
        body: "You need more resources. Research Mathematics up to level 2 to earn more per hour.",
        route: "/sciences",
        targetId: "science-mathematics",
        completion: { type: "scienceLevel", science: "mathematics", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 5750, minerals: 3250 } },
    },
    {
        id: "shipCargo",
        title: "Grow a Forager",
        body: "Let's grow an organic transport vessel for moving resources between your planets. Build at least one.",
        route: "/shipyard",
        targetId: "ship-1",
        completion: { type: "shipBuilt", shipId: 1, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 350, minerals: 700, gas: 350 } },
    },
    {
        id: "shipCargoUpgrade",
        title: "Upgrade your Forager",
        body: `The ${BUILDING_NAMES.techCenter} you built earlier upgrades ships — improvements apply globally, to every Forager you own or build from now on. Upgrade it to level 1 for +10% Cargo.`,
        route: "/starport",
        targetId: "ship-upgrade-1",
        completion: { type: "shipUpgradeLevel", shipId: 1, minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 1800, minerals: 900 } },
    },
    {
        id: "buildLaboratoryL2",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Astronomy needs a slightly better ${BUILDING_NAMES.laboratory}. Upgrade it to level 2.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 900, gas: 900 } },
    },
    {
        id: "scienceAstronomy",
        title: "Research Astronomy",
        body: "Astronomy increases the speed of your ships and is required for the Mite — and Intelligence, coming up shortly, needs it at level 2. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-astronomy",
        completion: { type: "scienceLevel", science: "astronomy", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 700, minerals: 500 } },
    },
    {
        id: "shipScout",
        title: "Grow a Mite",
        body: "A nimble bio-fighter that can slip past enemy armor entirely. Build at least one.",
        route: "/shipyard",
        targetId: "ship-3",
        completion: { type: "shipBuilt", shipId: 3, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 300, minerals: 600, gas: 300 } },
    },
    {
        id: "shipScoutUpgrade",
        title: "Upgrade your Mite",
        body: `War ships upgrade Attack and Life instead of Cargo. Upgrade your Mite to level 1 from the ${BUILDING_NAMES.techCenter} for +5% Attack and Life.`,
        route: "/starport",
        targetId: "ship-upgrade-3",
        completion: { type: "shipUpgradeLevel", shipId: 3, minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 800 } },
    },
    {
        id: "buildLaboratoryL3",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Chemistry needs an even better ${BUILDING_NAMES.laboratory}. Upgrade it to level 3.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 3 },
        reward: { type: "resources", amounts: { metal: 3500, minerals: 1750, gas: 450 } },
    },
    {
        id: "scienceChemistry",
        title: "Research Chemistry",
        body: "Chemistry reduces ship fuel consumption and unlocks Rocket Fuel research. Research it up to level 4.",
        route: "/sciences",
        targetId: "science-chemistry",
        completion: { type: "scienceLevel", science: "chemistry", minLevel: 4 },
        reward: { type: "resources", amounts: { metal: 500, minerals: 1000, gas: 500 } },
    },
    {
        id: "buildLaboratoryL4",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `A whole cluster of sciences — Intelligence, Signal Systems, Ecology, and Architecture — only need ${BUILDING_NAMES.laboratory} level 4. Upgrade it once and unlock all four.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 4 },
        reward: { type: "resources", amounts: { metal: 18000, minerals: 9000, gas: 750 } },
    },
    {
        id: "sciencePhysics",
        title: "Research Physics",
        body: "Physics increases Energy production, and Architecture (next up) needs it at level 5. Research it up to level 5.",
        route: "/sciences",
        targetId: "science-physics",
        completion: { type: "scienceLevel", science: "physics", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 16500, minerals: 6500 } },
    },
    {
        id: "scienceMathematicsL5",
        title: "Research Mathematics",
        body: "Architecture also needs Mathematics level 5. Research it up.",
        route: "/sciences",
        targetId: "science-mathematics",
        completion: { type: "scienceLevel", science: "mathematics", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 600, minerals: 450, gas: 150 } },
    },
    {
        id: "scienceEcology",
        title: "Research Ecology",
        body: `Ecology speeds up how quickly your planets recover ${RESOURCE_NAMES.ozone}. Research it up to level 1.`,
        route: "/sciences",
        targetId: "science-ecology",
        completion: { type: "scienceLevel", science: "ecology", minLevel: 1 },
        reward: { type: "resources", amounts: { minerals: 150, gas: 300 } },
    },
    {
        id: "scienceSignalSystems",
        title: "Research Signal Systems",
        body: "Signal Systems increases how many simultaneous fleet movements you can have in flight. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-signalSystems",
        completion: { type: "scienceLevel", science: "signalSystems", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 100, minerals: 200, gas: 100 } },
    },
    {
        id: "scienceIntelligence",
        title: "Research Intelligence",
        body: "Intelligence improves spying and is required for your Synapse. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-intelligence",
        completion: { type: "scienceLevel", science: "intelligence", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 5750, minerals: 3000 } },
    },
    {
        id: "scienceArchitecture",
        title: "Research Architecture",
        body: "Architecture reduces the cost of every building you upgrade from here on. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-architecture",
        completion: { type: "scienceLevel", science: "architecture", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 2250, minerals: 4500, gas: 2250 } },
    },
    {
        id: "buildLaboratoryL6",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Rocket Fuel needs a much better ${BUILDING_NAMES.laboratory}. Upgrade it to level 6.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 2250, minerals: 4250, gas: 850 } },
    },
    {
        id: "scienceRockets",
        title: "Research Rocket Fuel",
        body: "Rocket Fuel increases ship speed and is required by the Synapse. Research it up to level 3.",
        route: "/sciences",
        targetId: "science-rockets",
        completion: { type: "scienceLevel", science: "rockets", minLevel: 3 },
        reward: { type: "resources", amounts: { metal: 1500, minerals: 750, gas: 300 } },
    },
    {
        id: "buildShipyardL3",
        title: "Building better ships",
        body: `The Synapse needs an even better ${BUILDING_NAMES.shipyard}. Upgrade it to level 3.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 3 },
        reward: { type: "resources", amounts: { minerals: 600 } },
    },
    {
        id: "shipSpy",
        title: "Grow a Synapse",
        body: "An expendable psychic node for scouting enemy planets before you commit a fleet. Build at least one.",
        route: "/shipyard",
        targetId: "ship-12",
        completion: { type: "shipBuilt", shipId: 12, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 17500, gas: 17500 } },
    },
    {
        id: "scienceAstronomyL6",
        title: "Research Astronomy",
        body: "The Mantix needs Astronomy level 6.",
        route: "/sciences",
        targetId: "science-astronomy",
        completion: { type: "scienceLevel", science: "astronomy", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 1750, minerals: 1500 } },
    },
    {
        id: "shipCruiser",
        title: "Grow a Mantix",
        body: "A predatory hunter with devastating critical strikes. Build at least one.",
        route: "/shipyard",
        targetId: "ship-4",
        completion: { type: "shipBuilt", shipId: 4, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 11000, minerals: 5500, gas: 2250 } },
    },
    {
        id: "buildShipyardL5",
        title: "Building better ships",
        body: `The Big Forager needs a much more advanced ${BUILDING_NAMES.shipyard}. Upgrade it to level 5.`,
        route: "/zone/military",
        targetId: "building-shipyard",
        completion: { type: "buildingLevel", building: "shipyard", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 5000, minerals: 4000 } },
    },
    {
        id: "shipMegacargo",
        title: "The Big Forager",
        body: "A grown freight organism with an expanded cargo sac — useful once you're moving serious resources between planets, but not required right now.",
        route: "/shipyard",
        targetId: "ship-2",
        completion: { type: "manual" },
        reward: { type: "resources", amounts: { metal: 7250, minerals: 14500, gas: 6250 } },
    },
    {
        id: "shipColony",
        title: "Grow a Propagator",
        body: "Settles new planets, founding a colony on arrival — how you expand your empire beyond one world. Build at least one.",
        route: "/shipyard",
        targetId: "ship-10",
        completion: { type: "shipBuilt", shipId: 10, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 200, minerals: 100 } },
    },
    {
        id: "colonizeFlight",
        title: "Found your first colony",
        body: "Now send it out. Open the Galaxy map, tap an empty position, and launch a Colonize flight with your Propagator. When it arrives, a brand-new planet joins your empire — switch between your worlds from the location button in the top bar.",
        route: "/galaxy",
        completion: { type: "fleetsSent", missionType: "colonize" },
        reward: {
            type: "premium",
            label: "3-day Premium boost",
            grant: { boost: { tier: "premium", days: 3 }, darkMatter: TUTORIAL_DARK_MATTER_REWARD },
        },
    },
    {
        id: "scienceDefenceSystems",
        title: "Research Defence Systems",
        body: "The Miner needs a bit of Defence Systems. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-defenceSystems",
        completion: { type: "scienceLevel", science: "defenceSystems", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 16500, minerals: 32500, gas: 6500 } },
    },
    {
        id: "scienceRocketsL6",
        title: "Research Rocket Fuel",
        body: "The Miner also needs more Rocket Fuel. Research it up to level 6.",
        route: "/sciences",
        targetId: "science-rockets",
        completion: { type: "scienceLevel", science: "rockets", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 6250, minerals: 3250 } },
    },
    {
        id: "shipRecycler",
        title: "Grow a Miner",
        body: `Harvests ${RESOURCE_NAMES.scrap} and debris left behind after battles — send it out after any fight to reclaim resources. Build at least one.`,
        route: "/shipyard",
        targetId: "ship-11",
        completion: { type: "shipBuilt", shipId: 11, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 3500, minerals: 1750 } },
    },
    {
        id: "scienceDefenceSystemsL6",
        title: "Research Defence Systems",
        body: "The Chiton needs much more Defence Systems. Research it up to level 6.",
        route: "/sciences",
        targetId: "science-defenceSystems",
        completion: { type: "scienceLevel", science: "defenceSystems", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 1750, minerals: 4750 } },
    },
    {
        id: "shipGuardian",
        title: "Grow your Chiton",
        body: "A defensive organism that bolsters your whole fleet's life. Build at least one.",
        route: "/shipyard",
        targetId: "ship-5",
        completion: { type: "shipBuilt", shipId: 5, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 2500, minerals: 4750, gas: 2500 } },
    },
    {
        id: "buildLaboratoryL7",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Aerodynamics needs a more advanced ${BUILDING_NAMES.laboratory}. Upgrade it to level 7.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 7 },
        reward: { type: "resources", amounts: { metal: 11500, minerals: 5750, gas: 1500 } },
    },
    {
        id: "scienceChemistryL6",
        title: "Research Chemistry",
        body: `The ${BUILDING_NAMES.recyclingPlant} needs more Chemistry. Research it up to level 6.`,
        route: "/sciences",
        targetId: "science-chemistry",
        completion: { type: "scienceLevel", science: "chemistry", minLevel: 6 },
        reward: { type: "resources", amounts: { metal: 13000, minerals: 8500, gas: 3000 } },
    },
    {
        id: "buildRecyclingPlant",
        title: `Build the ${BUILDING_NAMES.recyclingPlant}`,
        body: `Converts ${RESOURCE_NAMES.scrap} into ships in parallel with your ${BUILDING_NAMES.shipyard}, speeding up production. Bring it to level 1.`,
        route: "/zone/industry",
        targetId: "building-recyclingPlant",
        completion: { type: "buildingLevel", building: "recyclingPlant", minLevel: 1 },
        reward: { type: "resources", amounts: { minerals: 4250, gas: 2250 } },
    },
    {
        id: "scienceAerodynamics",
        title: "Research Aerodynamics",
        body: "Aerodynamics increases ship speed and is required for the Spearhead. Research it up to level 3.",
        route: "/sciences",
        targetId: "science-aerodynamics",
        completion: { type: "scienceLevel", science: "aerodynamics", minLevel: 3 },
        reward: { type: "resources", amounts: { metal: 19000, minerals: 8250 } },
    },
    {
        id: "shipBattlecruiser",
        title: "Grow a Spearhead",
        body: "A phasing warform that hardens allied hulls. Build at least one.",
        route: "/shipyard",
        targetId: "ship-6",
        completion: { type: "shipBuilt", shipId: 6, minCount: 1 },
        reward: { type: "resources", amounts: { metal: 4250, minerals: 8250, gas: 4250 } },
    },
    {
        id: "buildLaboratoryL8",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Laser, Ion, Plasma, and Polymer Shields — your gateway to stronger weapons and armor — all become available at once here. Upgrade the ${BUILDING_NAMES.laboratory} to level 8.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 8 },
        reward: { type: "resources", amounts: { metal: 70000, minerals: 59000 } },
    },
    {
        id: "scienceLaserL10",
        title: "Research Laser",
        body: "Laser increases the attack of laser-armed ships and is a building block for Ion and Plasma. Research it up to level 10.",
        route: "/sciences",
        targetId: "science-laser",
        completion: { type: "scienceLevel", science: "laser", minLevel: 10 },
        reward: { type: "resources", amounts: { metal: 9000, minerals: 4500, gas: 900 } },
    },
    {
        id: "scienceIonL5",
        title: "Research Ion",
        body: "Ion increases the attack of ion-armed ships and is also required for Plasma. Research it up to level 5.",
        route: "/sciences",
        targetId: "science-ion",
        completion: { type: "scienceLevel", science: "ion", minLevel: 5 },
        reward: { type: "resources", amounts: { metal: 56000, minerals: 22500 } },
    },
    {
        id: "scienceMathematicsL7",
        title: "Research Mathematics",
        body: "Plasma needs Mathematics level 7. Research it up.",
        route: "/sciences",
        targetId: "science-mathematics",
        completion: { type: "scienceLevel", science: "mathematics", minLevel: 7 },
        reward: { type: "resources", amounts: { metal: 1750, minerals: 1750, gas: 1750 } },
    },
    {
        id: "sciencePlasma",
        title: "Research Plasma",
        body: "Plasma increases the attack of plasma-armed ships. Research it up to level 2.",
        route: "/sciences",
        targetId: "science-plasma",
        completion: { type: "scienceLevel", science: "plasma", minLevel: 2 },
        reward: { type: "resources", amounts: { metal: 600, minerals: 300, gas: 150 } },
    },
    {
        id: "sciencePolymerShields",
        title: "Research Polymer Shields",
        body: "Polymer Shields increases the armor of light-hulled ships and defences. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-polymerShields",
        completion: { type: "scienceLevel", science: "polymerShields", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 7000, minerals: 14000, gas: 7000 } },
    },
    {
        id: "buildLaboratoryL9",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Carbon Fiber Shields needs ${BUILDING_NAMES.laboratory} level 9. Upgrade it.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 9 },
        reward: { type: "resources", amounts: { metal: 750, minerals: 400, gas: 200 } },
    },
    {
        id: "scienceCarbonFiberShields",
        title: "Research Carbon Fiber Shields",
        body: "Carbon Fiber Shields increases the armor of medium-hulled ships and defences. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-carbonFiberShields",
        completion: { type: "scienceLevel", science: "carbonFiberShields", minLevel: 1 },
        reward: { type: "resources", amounts: { metal: 12000, minerals: 23500, gas: 12000 } },
    },
    {
        id: "buildLaboratoryL10",
        title: `Upgrading ${BUILDING_NAMES.laboratory}`,
        body: `Heavy Metal Shields needs your highest ${BUILDING_NAMES.laboratory} level yet. Upgrade it to level 10.`,
        route: "/zone/military",
        targetId: "building-laboratory",
        completion: { type: "buildingLevel", building: "laboratory", minLevel: 10 },
        reward: { type: "resources", amounts: { metal: 950, minerals: 500, gas: 250 } },
    },
    {
        id: "scienceHeavyMetalShields",
        title: "Research Heavy Metal Shields",
        body: "Heavy Metal Shields increases the armor of heavy-hulled ships and defences — the last science on this tour. Research it up to level 1.",
        route: "/sciences",
        targetId: "science-heavyMetalShields",
        completion: { type: "scienceLevel", science: "heavyMetalShields", minLevel: 1 },
    },
    {
        id: "exploreMore",
        title: "More to explore",
        body: "Fleets, the Galaxy map, Alliances, and Rankings are all here too. Sending your fleet to attack, trade, or explore — and the battles that follow — are coming in future updates. For now, everything above is enough to build a strong, defensible empire.",
        route: "/planet",
        targetId: "bottom-nav",
        completion: { type: "manual" },
    },
    {
        id: "planetNameChange",
        title: "Rename your planet",
        body: "Give your homeworld a name of your own from its Edit Name button.",
        route: "/planet",
        targetId: "planet-edit-name",
        completion: { type: "manual" },
    },
    {
        id: "planetPhotoChange",
        title: "Change your planet's photo",
        body: "Pick a different look for your homeworld from its Change Photo button.",
        route: "/planet",
        targetId: "planet-edit-photo",
        completion: { type: "manual" },
    },
    {
        id: "profileNameChange",
        title: "Rename your commander",
        body: "Head to your Personal page and set your commander's display name — it's what other players see on the ranking board.",
        route: "/personal",
        targetId: "profile-edit-name",
        completion: { type: "manual" },
    },
    {
        id: "profilePhotoChange",
        title: "Add a profile photo",
        body: "Upload a photo so other commanders recognize you.",
        route: "/personal",
        targetId: "profile-edit-photo",
        completion: { type: "manual" },
    },
    ALLIANCE_INTRO_STEP,
];
const RACE_TUTORIAL_STEPS = {
    humans: HUMANS_STEPS,
    robots: ROBOTS_STEPS,
    axons: AXONS_STEPS,
};
/**
 * The alliance chapter every race finishes on. Every condition reads the
 * player's own alliance, so a plain member qualifies as soon as their alliance
 * does. `winRound` is the last step and has no reward of its own — the round
 * victory pays its Dark Matter.
 */
export const TEAM_TUTORIAL_STEPS = [
    {
        id: "joinTeam",
        title: "Join an alliance",
        body: `You need to be in an alliance. Browse the alliances here and apply to one, or found your own — founding needs the ${BUILDING_NAMES.government} at Level ${ALLIANCE_REQUIRED_BUILDING_LEVEL}. Everything from here on is done together with your team.`,
        route: "/alliance",
        targetId: "alliance-join",
        completion: { type: "allianceJoined" },
        reward: { type: "resources", amounts: { metal: 30000, minerals: 30000, gas: 15000 } },
    },
    {
        id: "teamPlanet",
        title: "Found a Team planet",
        body: `Your alliance must have a Team planet. Its leader needs the ${BUILDING_NAMES.government} at Level ${TEAM_PLANET_REQUIRED_BUILDING_LEVEL} on any planet and a planet that isn't their main one, which the leader converts into the Team planet. Everything on the converted planet resets, so choose a colony you can spare.`,
        route: "/alliance",
        targetId: "alliance-team-planet",
        completion: { type: "teamPlanetFounded" },
        reward: { type: "resources", amounts: { metal: 50000, minerals: 50000, gas: 25000 } },
    },
    {
        id: "sendSunAttack",
        title: "Send a Sun Attack",
        body: `Your alliance must have sent a Sun Attack. It is sent from the Team planet, from the Galaxy map, and the fleet must carry at least ${formatCount(SUN_ATTACK_MIN_POPULATION)} population. Pick a Sun no one controls and launch. Once your alliance has sent one, this step counts, even if the fleet is lost.`,
        route: "/galaxy",
        completion: { type: "fleetsSent", missionType: "sunAttack" },
        reward: { type: "resources", amounts: { metal: 70000, minerals: 70000, gas: 35000 } },
    },
    {
        id: "controlSun1",
        title: "Take your first Sun",
        body: `Your alliance must control at least 1 Sun. Winning the battle at a Sun gives your alliance a claim; the claim becomes control once a garrison of ${formatCount(SUN_CONTROL_CLAIM_POPULATION)} population reaches the Sun within ${SUN_CONTROL_CLAIM_WINDOW_HOURS} hours. Expect reclaims: after ${SUN_CONTROL_RECLAIM_IMMUNITY_HOURS} hours of immunity, and once the ${SUN_ATTACK_COOLDOWN_HOURS}-hour cooldown after the last battle has passed, another alliance may attack a Sun you hold. Holding off a reclaim makes it a stronghold; losing one hands the attacker a claim.`,
        route: "/alliance",
        targetId: "alliance-sun-count",
        completion: { type: "sunsControlled", minCount: 1 },
        reward: { type: "resources", amounts: { metal: 70000, minerals: 70000, gas: 50000 } },
    },
    {
        id: "controlSuns3",
        title: "Hold three Suns",
        body: `Your alliance must control at least 3 Suns at once. Keep every garrison at ${formatCount(SUN_CONTROL_MAINTAIN_POPULATION)} population or more: a stronghold that falls below it has ${SUN_CONTROL_TOP_UP_WINDOW_HOURS} hours to be topped up or control lapses.`,
        route: "/alliance",
        targetId: "alliance-sun-count",
        completion: { type: "sunsControlled", minCount: 3 },
        reward: { type: "resources", amounts: { metal: 70000, minerals: 70000, gas: 70000 } },
    },
    {
        id: "winRound",
        title: "Win the round",
        body: `Your alliance must win the round. Holding ${SUNS_TO_WIN} Suns starts a ${SUN_VICTORY_CONTEST_HOURS}-hour countdown during which rivals can only dispatch reclaims; hold all ${SUNS_TO_WIN} through it and the round is yours.`,
        completedBody: "You have won the round by completing this tutorial. Your reward will be added to your account soon.",
        route: "/alliance",
        targetId: "alliance-sun-count",
        completion: { type: "roundWon" },
    },
];
/** Full step list for `race`: the shared prefix, that race's continuation, then the team chapter. */
export function getTutorialSteps(race) {
    return [...SHARED_TUTORIAL_STEPS, ...RACE_TUTORIAL_STEPS[race], ...TEAM_TUTORIAL_STEPS];
}
//# sourceMappingURL=tutorial.js.map